import { AnyPlan } from '../../types';
import { OfflineRow } from './types';

export const getCountryData = (plan: AnyPlan, country: string) =>
  plan.countrySetup?.[country];

export const getOfflineList = (plan: AnyPlan, c: string): OfflineRow[] =>
  (getCountryData(plan, c)?.offline?.tv?.offlineBudget ?? []) as OfflineRow[];

export const formatTouchpointLabel = (touchpoint: string) => {
  const lower = touchpoint.toLowerCase();
  if (lower === 'tv') return 'TV';
  if (lower === 'outdoor' || lower === 'ooh') return 'OOH';
  return touchpoint.toUpperCase();
};

export const getOfflineTouchpoints = (allPlans: AnyPlan[], c: string) => {
  const set = new Set<string>();

  allPlans.forEach((plan) => {
    const list = getOfflineList(plan, c);
    list.forEach((row) => {
      if (!row || row.touchpoint === 'tech cost') return;
      set.add(row.touchpoint);
    });
  });

  return Array.from(set);
};

export const getOfflineEntryForPlan = (
  plan: AnyPlan,
  c: string,
  touchpoint: string
): OfflineRow | undefined => {
  const list = getOfflineList(plan, c);
  return list.find((row) => row.touchpoint === touchpoint);
};

const isOOH = (touchpoint: string) => {
  const tp = touchpoint.toLowerCase();
  return tp === 'outdoor' || tp === 'ooh';
};

const getOOHType = (ch: any) => String(ch?.type ?? ch?.format ?? '');
const getOOHOption = (ch: any) => String(ch?.option ?? ch?.variant ?? '');
const oohVariantKey = (ch: any) => `${getOOHType(ch)}||${getOOHOption(ch)}`;

const getTVCopyName = (ch: any) =>
  String(ch?.copy?.name ?? `Copy ${ch?.copy?.id ?? ch?.id ?? ''}`);

const tvRowKey = (name: string, idx: number) => `${name}||${idx}`;

const parseTvRowKey = (key: string): { name: string; idx: number } => {
  const [name, idxStr] = key.split('||');
  const idx = Number(idxStr);
  return { name: name ?? '', idx: Number.isFinite(idx) ? idx : 0 };
};

const sortTVChildrenStable = (a: any, b: any) => {
  const aId = String(a?.copy?.id ?? a?.id ?? '');
  const bId = String(b?.copy?.id ?? b?.id ?? '');
  return aId.localeCompare(bId);
};

export const getChildRowsForTouchpoint = (
  allPlans: AnyPlan[],
  c: string,
  touchpoint: string
) => {
  const oohMap = new Map<string, { key: string; name: string }>();
  const oohIncludedAnyPlan = new Set<string>();

  if (isOOH(touchpoint)) {
    allPlans.forEach((plan) => {
      const entry = getOfflineEntryForPlan(plan, c, touchpoint);
      (entry?.children ?? []).forEach((ch: any) => {
        const key = oohVariantKey(ch);
        if (!key || key === '||') return;
        if (ch?.isIncludedInCalculations === true) oohIncludedAnyPlan.add(key);
      });
    });

    allPlans.forEach((plan) => {
      const entry = getOfflineEntryForPlan(plan, c, touchpoint);
      const children = entry?.children ?? [];

      children.forEach((ch: any) => {
        const key = oohVariantKey(ch);
        if (!oohIncludedAnyPlan.has(key)) return;

        const option = getOOHOption(ch);
        const label = option || 'N/A';

        if (!oohMap.has(key)) oohMap.set(key, { key, name: label });
      });
    });

    return Array.from(oohMap.values());
  }

  const maxCountByName = new Map<string, number>();

  allPlans.forEach((plan) => {
    const entry = getOfflineEntryForPlan(plan, c, touchpoint);
    const children = [...(entry?.children ?? [])].sort(sortTVChildrenStable);

    const countByName = new Map<string, number>();
    children.forEach((ch: any) => {
      const name = getTVCopyName(ch);
      countByName.set(name, (countByName.get(name) ?? 0) + 1);
    });

    countByName.forEach((count, name) => {
      const prev = maxCountByName.get(name) ?? 0;
      if (count > prev) maxCountByName.set(name, count);
    });
  });

  const rows: Array<{ key: string; name: string }> = [];
  Array.from(maxCountByName.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([name, maxCount]) => {
      for (let i = 0; i < maxCount; i += 1) {
        const label = name;
        rows.push({ key: tvRowKey(name, i), name: label });
      }
    });

  return rows;
};

export const getChildForEntry = (
  entry: OfflineRow | undefined,
  childKey: string,
  touchpoint?: string
): any | undefined => {
  if (!entry?.children) return undefined;

  // ---------- OOH ----------
  if (touchpoint && isOOH(touchpoint)) {
    return (entry.children as any[]).find((ch: any) => {
      if (oohVariantKey(ch) !== childKey) return false;
      return ch?.isIncludedInCalculations === true;
    });
  }

  const { name, idx } = parseTvRowKey(childKey);

  const children = [...(entry.children as any[])].sort(sortTVChildrenStable);
  const matches = children.filter((ch: any) => getTVCopyName(ch) === name);

  return matches[idx];
};
